import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CurrencyContext = createContext();

const FALLBACK_RATES = {
  AED: { rate: 1, symbol: 'AED', name: 'UAE Dirham' },
  USD: { rate: 0.2723, symbol: '$', name: 'US Dollar' },
  EUR: { rate: 0.2510, symbol: '\u20ac', name: 'Euro' },
  GBP: { rate: 0.2148, symbol: '\u00a3', name: 'British Pound' },
  INR: { rate: 22.82, symbol: '\u20b9', name: 'Indian Rupee' },
};

export const CurrencyProvider = ({ children }) => {
  // Default to the user's bound region currency (UAE → AED, IN → INR).
  // Falls back to AED if no user is logged in or no region is set, which
  // is fine for the public-facing landing pages.
  const regionFromUser = (() => {
    try {
      const raw = localStorage.getItem('dl_user');
      if (!raw) return null;
      const u = JSON.parse(raw);
      return (u?.region || '').toUpperCase() || null;
    } catch { return null; }
  })();
  const initialCurrency = regionFromUser === 'IN' ? 'INR' : 'AED';

  const [currency, setCurrencyState] = useState(initialCurrency);
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const [ratesRes, prefRes] = await Promise.all([
          axios.get(`${API}/currency/rates`).catch(() => null),
          axios.get(`${API}/currency/preference`).catch(() => null),
        ]);
        if (ratesRes?.data?.rates) setRates(ratesRes.data.rates);
        // Server preference wins if explicitly set, otherwise keep the
        // region-derived default we computed at construction time.
        if (prefRes?.data?.currency) setCurrencyState(prefRes.data.currency);
      } catch (e) { /* use fallbacks */ }
      setLoaded(true);
    };
    init();
  }, []);

  const setCurrency = useCallback(async (code) => {
    setCurrencyState(code);
    try { await axios.put(`${API}/currency/preference`, { currency: code }); } catch (e) { /* silent */ }
  }, []);

  // Convert AED amount to selected currency
  const convert = useCallback((aedAmount) => {
    if (!aedAmount || currency === 'AED') return aedAmount;
    const r = rates[currency]?.rate || 1;
    return aedAmount * r;
  }, [currency, rates]);

  // Convert from selected currency back to AED
  const toAED = useCallback((amount) => {
    if (!amount || currency === 'AED') return amount;
    const r = rates[currency]?.rate || 1;
    return amount / r;
  }, [currency, rates]);

  // Format with smart abbreviation
  // INR uses Lakhs/Crores: ₹50L, ₹1.2Cr, ₹150Cr
  // Others use M/B: $6.8M, AED 25M
  const fmt = useCallback((amount, sym) => {
    if (!amount && amount !== 0) return `${sym}0`;
    const abs = Math.abs(amount);
    const isINR = currency === 'INR';

    if (isINR) {
      if (abs >= 10_000_000) return `${sym}${(amount / 10_000_000).toFixed(abs >= 100_000_000 ? 0 : 1)}Cr`;
      if (abs >= 100_000) return `${sym}${(amount / 100_000).toFixed(abs >= 1_000_000 ? 0 : 1)}L`;
      if (abs >= 1_000) return `${sym}${Math.round(amount).toLocaleString('en-IN')}`;
      return `${sym}${amount.toFixed(2)}`;
    }

    if (abs >= 1_000_000_000) return `${sym}${(amount / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${sym}${(amount / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sym}${Math.round(amount).toLocaleString()}`;
    return `${sym}${amount.toFixed(2)}`;
  }, [currency]);

  // Primary format: user's selected currency
  const formatPrice = useCallback((aedAmount, opts = {}) => {
    if (!aedAmount && aedAmount !== 0) return '';
    const { compact = true, aedOnly = false } = opts;
    const sym = rates[currency]?.symbol || currency;

    if (currency === 'AED' || aedOnly) {
      return compact ? fmt(aedAmount, 'AED ') : `AED ${Math.round(aedAmount).toLocaleString()}`;
    }

    const converted = convert(aedAmount);
    return compact ? fmt(converted, sym) : `${sym}${Math.round(converted).toLocaleString()}`;
  }, [currency, rates, convert, fmt]);

  // Secondary line: AED value (returns empty string if already AED)
  const formatPriceAED = useCallback((aedAmount, opts = {}) => {
    if (!aedAmount && aedAmount !== 0) return '';
    if (currency === 'AED') return '';
    const { compact = true } = opts;
    return compact ? fmt(aedAmount, 'AED ') : `AED ${Math.round(aedAmount).toLocaleString()}`;
  }, [currency, fmt]);

  // Get just the converted number (for inputs/sliders)
  const convertedValue = useCallback((aedAmount) => {
    if (!aedAmount) return 0;
    return Math.round(convert(aedAmount));
  }, [convert]);

  const value = {
    currency,
    setCurrency,
    rates,
    convert,
    toAED,
    formatPrice,
    formatPriceAED,
    convertedValue,
    symbol: rates[currency]?.symbol || currency,
    loaded,
    availableCurrencies: Object.entries(rates).map(([code, data]) => ({
      code, symbol: data.symbol, name: data.name,
    })),
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};
